import { ArrowLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../auth-context';
import { t } from '../../i18n';
import type { SurahMeta, TodayResponse } from '../../types';
import { AppShell } from '../shared/AppShell';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { useToast } from '../shared/Toast';

export function SurahPicker() {
  const navigate = useNavigate();
  const { state } = useAuth();
  const toast = useToast();
  const child = state.status === 'child' ? state.child : null;

  const [surahs, setSurahs] = useState<SurahMeta[] | null>(null);
  const [today, setToday] = useState<TodayResponse | null>(null);
  const [showList, setShowList] = useState(false);
  const [query, setQuery] = useState('');
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.surahs().then((r) => setSurahs(r.surahs));
    api.today().then(setToday);
  }, []);

  const current = today?.current_surah;
  const next = current && surahs ? surahs[Math.min(current.number, 113)] : null;

  const filtered = useMemo(() => {
    if (!surahs) return [];
    const q = query.trim().toLowerCase();
    if (!q) return surahs;
    return surahs.filter(
      (s) => s.name_uz.toLowerCase().includes(q) || String(s.number) === q,
    );
  }, [surahs, query]);

  if (!child) return null;

  const pick = async (surah_number: number) => {
    setSubmitting(true);
    try {
      await api.setCurrentSurah(surah_number);
      navigate('/child/record');
    } catch (err) {
      toast.show(t('common.error.network'), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const completeSurah = async () => {
    if (!current) return;
    try {
      const res = await api.completeSurah(current.number);
      setConfirmComplete(false);
      navigate('/child/surah-complete', { state: res });
    } catch {
      toast.show(t('common.error.network'), 'danger');
    }
  };

  return (
    <AppShell ramp={child.color_ramp}>
      <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-5 p-5">
        <header className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label={t('common.back')} className="text-ink-muted">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[18px] font-medium">{t('surah.picker.title')}</h1>
        </header>

        {current ? (
          <Button childColor fullWidth size="lg" disabled={submitting} onClick={() => pick(current.number)}>
            {t('surah.picker.continue', { surah: current.name_uz })}
          </Button>
        ) : null}

        {next ? (
          <Button variant="secondary" childColor fullWidth disabled={submitting} onClick={() => pick(next.number)}>
            {t('surah.picker.next', { surah: next.name_uz })}
          </Button>
        ) : null}

        <button
          onClick={() => setShowList((v) => !v)}
          className="text-[13px] text-ink-muted active:scale-[0.98]"
        >
          {t('surah.picker.choose_other')}
        </button>

        {showList ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 rounded-md border-hairline border-border-strong bg-surface px-3">
              <Search size={16} className="text-ink-subtle" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('surah.picker.search')}
                className="h-11 flex-1 bg-transparent text-[14px] outline-none"
              />
            </div>
            <ul className="flex max-h-[360px] flex-col overflow-y-auto rounded-md border-hairline border-border bg-surface">
              {filtered.map((s) => (
                <li key={s.number}>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => pick(s.number)}
                    className="flex w-full items-center gap-3 border-b-hairline border-border px-3 py-2.5 text-left last:border-b-0 active:scale-[0.99]"
                  >
                    <span className="w-8 text-[12px] text-ink-subtle">{s.number}</span>
                    <span className="flex-1 text-[14px]">{s.name_uz}</span>
                    <span className="font-arabic text-[18px] text-ink-muted" dir="rtl">{s.name_ar}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {current ? (
          <button
            onClick={() => setConfirmComplete(true)}
            className="mt-auto text-[13px] text-ink-muted underline-offset-2 hover:underline active:scale-[0.98]"
          >
            Surani tugatdim
          </button>
        ) : null}
      </main>

      <Modal
        open={confirmComplete}
        onClose={() => setConfirmComplete(false)}
        title={
          current
            ? t('surah.complete.confirm', { surah: current.name_uz })
            : undefined
        }
      >
        <div className="flex flex-col gap-2">
          <Button onClick={completeSurah} fullWidth>
            {t('common.confirm')}
          </Button>
          <Button variant="ghost" fullWidth onClick={() => setConfirmComplete(false)}>
            {t('common.cancel')}
          </Button>
        </div>
      </Modal>
    </AppShell>
  );
}
